import useMatrixPagination from "./hooks/useMatrixPagination";
import useScoreCards from "./hooks/useScoreCards";
import MatrixWithLabel from "./MatrixWithLabel";
import PaginationControls from "./PaginationControls";
import { DiffProps } from "./types";

const getMatrixLabel = (
  showDiff: boolean,
  type: string,
  index: number,
  length: number,
): string | undefined => {
  if (!showDiff) {
    return undefined;
  }

  const capitalizedType = type.charAt(0).toUpperCase() + type.slice(1);

  if (length > 1) {
    return `${capitalizedType} (${index + 1})`;
  }

  return capitalizedType;
};

const ScoreMatrix = ({
  beforeScore,
  afterScore,
  showDiff = true,
  hideTitle = false,
  showAll = false,
  showScore = false,
  showBefore = true,
  collapsed = true,
  minimal = false,
}: DiffProps & {
  showAll?: boolean;
  showScore?: boolean;
  showBefore?: boolean;
  collapsed?: boolean;
  minimal?: boolean;
}) => {
  const { currentPage, maxPages, handlePrevPage, handleNextPage } = useMatrixPagination(
    beforeScore,
    afterScore,
  );
  const scoreCards = useScoreCards(beforeScore, afterScore, showAll, currentPage);

  return (
    <div className="flex w-full flex-col items-center">
      <div className="relative w-full">
        {showAll ? (
          <div className="flex flex-col gap-4 lg:flex-row lg:gap-6">
            {["before", "after"].map(type => {
              if (type === "after" && !showDiff) {
                return null;
              }
              const isBefore = type === "before";
              const scoreMatrix = isBefore ? beforeScore?.score_matrix : afterScore?.score_matrix;
              const cards = isBefore ? scoreCards.before : scoreCards.after;
              const compareCards = isBefore ? scoreCards.after : scoreCards.before;
              const score = isBefore ? beforeScore?.score : afterScore?.score;
              const { length } = cards;

              if (!showBefore && type === "before") {
                return null;
              }

              return (
                <div key={type} className="flex w-full flex-col gap-4">
                  {scoreMatrix
                    ? cards.map((scoreCard, index) =>
                        scoreCard.length === 0 ? (
                          <MatrixWithLabel
                            // eslint-disable-next-line react/no-array-index-key
                            key={`${type}-placeholder-${index}`}
                            label={
                              length > 1
                                ? `${type.charAt(0).toUpperCase() + type.slice(1)} (${index + 1})`
                                : type.charAt(0).toUpperCase() + type.slice(1)
                            }
                            type={type as "before" | "after"}
                            showDiff={showDiff}
                            showScore={showScore}
                            collapsed={collapsed}
                            minimal={minimal}
                          />
                        ) : (
                          <MatrixWithLabel
                            // eslint-disable-next-line react/no-array-index-key
                            key={`${type}-${index}`}
                            scoreCard={scoreCard}
                            compareCard={compareCards[index]}
                            type={type as "before" | "after"}
                            showDiff={showDiff}
                            label={getMatrixLabel(showDiff, type, index, length)}
                            showScore={showScore}
                            score={score}
                            collapsed={collapsed}
                            minimal={minimal}
                          />
                        ),
                      )
                    : Array.from({ length: compareCards.length }).map((_, index) => (
                        <MatrixWithLabel
                          // eslint-disable-next-line react/no-array-index-key
                          key={`${type}-placeholder-${index}`}
                          label={
                            compareCards.length > 1
                              ? `${type.charAt(0).toUpperCase() + type.slice(1)} (${index + 1})`
                              : type.charAt(0).toUpperCase() + type.slice(1)
                          }
                          type={type as "before" | "after"}
                          showDiff={showDiff}
                          showScore={showScore}
                          collapsed={collapsed}
                          minimal={minimal}
                        />
                      ))}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="flex w-full flex-col gap-4">
            {!showAll && maxPages > 1 && (
              <PaginationControls
                currentPage={currentPage}
                maxPages={maxPages}
                onPrevPage={handlePrevPage}
                onNextPage={handleNextPage}
              />
            )}
            <div className="flex w-full flex-col gap-4">
              {beforeScore?.score_matrix ? (
                <MatrixWithLabel
                  scoreCard={scoreCards.before[0]}
                  compareCard={scoreCards.after[0]}
                  type="before"
                  showDiff={showDiff}
                  label={showDiff && !hideTitle ? "Before" : undefined}
                  showScore={showScore}
                  score={beforeScore.score}
                  collapsed={collapsed}
                  minimal={minimal}
                />
              ) : (
                showDiff && (
                  <div className="flex h-full items-center justify-center">
                    <MatrixWithLabel
                      type="before"
                      showDiff={showDiff}
                      label="Before"
                      showScore={showScore}
                      collapsed={collapsed}
                      minimal={minimal}
                    />
                  </div>
                )
              )}
              {showDiff &&
                !showScore &&
                (afterScore?.score_matrix ? (
                  <MatrixWithLabel
                    scoreCard={scoreCards.after[0]}
                    compareCard={scoreCards.before[0]}
                    type="after"
                    showDiff={showDiff}
                    label="After"
                    showScore={showScore}
                    score={afterScore.score}
                    collapsed={collapsed}
                    minimal={minimal}
                  />
                ) : (
                  <div
                    className="flex h-full items-center justify-center"
                    style={{
                      width: beforeScore?.score_matrix ? "100%" : "auto",
                    }}
                  >
                    <MatrixWithLabel
                      type="after"
                      showDiff={showDiff}
                      label="After"
                      showScore={showScore}
                      collapsed={collapsed}
                      minimal={minimal}
                    />
                  </div>
                ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ScoreMatrix;
